import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Pencil, Trash2, Plus, Ticket } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import ExportButtons from './ExportButtons';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const CouponManager = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('membership'); // 'membership', 'listing', or 'shop'
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [couponToDelete, setCouponToDelete] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    coupon_type: 'membership', // 'membership' or 'shop'
    discount_type: 'percentage',
    discount_value: '',
    max_uses: '',
    expires_at: '',
    min_purchase: '0',
    applicable_to: 'all',
  });

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  // Helper function to get coupon effective status with color
  const getCouponStatus = (coupon) => {
    // Use backend-computed effective_status if available
    if (coupon.effective_status) {
      switch (coupon.effective_status) {
        case 'expired':
          return { label: 'Expired', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', isUsable: false };
        case 'exhausted':
          return { label: 'Exhausted', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400', isUsable: false };
        case 'inactive':
          return { label: 'Inactive', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400', isUsable: false };
        case 'active':
          return { label: 'Active', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', isUsable: true };
        default:
          return { label: 'Unknown', color: 'bg-gray-100 text-gray-800', isUsable: false };
      }
    }
    
    // Fallback: compute status client-side
    const now = new Date();
    
    // Check expiration
    if (coupon.expires_at) {
      const expiresAt = new Date(coupon.expires_at);
      if (expiresAt < now) {
        return { label: 'Expired', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', isUsable: false };
      }
    }
    
    // Check max uses
    if (coupon.max_uses && coupon.current_uses >= coupon.max_uses) {
      return { label: 'Exhausted', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400', isUsable: false };
    }
    
    // Check manual active flag
    if (!coupon.active) {
      return { label: 'Inactive', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400', isUsable: false };
    }
    
    return { label: 'Active', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', isUsable: true };
  };

  // Format expiration date with warning
  const formatExpirationDate = (expiresAt) => {
    if (!expiresAt) return 'No expiration';
    
    const date = new Date(expiresAt);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    
    const formattedDate = date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
    
    if (daysUntilExpiry < 0) {
      return <span className="text-red-600 font-medium">{formattedDate} (Expired)</span>;
    } else if (daysUntilExpiry <= 7) {
      return <span className="text-orange-600 font-medium">{formattedDate} ({daysUntilExpiry} days left)</span>;
    }
    
    return formattedDate;
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/coupons`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setCoupons(data.coupons || []);
      }
    } catch (error) {
      console.error('Error fetching coupons:', error);
      toast({ title: 'Error', description: 'Failed to load coupons', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCoupon = async () => {
    try {
      if (!formData.name || !formData.code || !formData.discount_value) {
        toast({ title: 'Error', description: 'Please fill all required fields', variant: 'destructive' });
        return;
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/coupons`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          discount_value: parseFloat(formData.discount_value),
          max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
          min_purchase: parseFloat(formData.min_purchase || 0)
        })
      });

      if (response.ok) {
        toast({ title: 'Success', description: 'Coupon created successfully' });
        setIsCreateModalOpen(false);
        resetForm();
        fetchCoupons();
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.detail || 'Failed to create coupon', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to create coupon', variant: 'destructive' });
    }
  };

  const handleUpdateCoupon = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/coupons/${editingCoupon.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          discount_value: parseFloat(formData.discount_value),
          max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
          min_purchase: parseFloat(formData.min_purchase || 0)
        })
      });

      if (response.ok) {
        toast({ title: 'Success', description: 'Coupon updated successfully' });
        setIsEditModalOpen(false);
        setEditingCoupon(null);
        resetForm();
        fetchCoupons();
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.detail || 'Failed to update coupon', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update coupon', variant: 'destructive' });
    }
  };

  const handleDeleteCoupon = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/coupons/${couponToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast({ title: 'Success', description: 'Coupon deleted successfully' });
        setDeleteConfirmOpen(false);
        setCouponToDelete(null);
        fetchCoupons();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete coupon', variant: 'destructive' });
    }
  };

  const openEditModal = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      name: coupon.name,
      code: coupon.code,
      coupon_type: coupon.coupon_type || 'membership',
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value.toString(),
      max_uses: coupon.max_uses?.toString() || '',
      expires_at: coupon.expires_at || '',
      min_purchase: coupon.min_purchase?.toString() || '0',
      applicable_to: coupon.applicable_to || 'all',
    });
    setIsEditModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      discount_type: 'percentage',
      discount_value: '',
      max_uses: '',
      expires_at: '',
      min_purchase: '0',
      applicable_to: 'all',
    });
  };

  if (loading) {
    return <div className="p-6">Loading coupons...</div>;
  }

  // Filter coupons by type
  const filteredCoupons = coupons.filter(coupon => coupon.coupon_type === activeTab);

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Coupon Management</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Create and manage discount coupons for memberships and shop</p>
        </div>
        <Button onClick={() => {
          setFormData({ ...formData, coupon_type: activeTab });
          setIsCreateModalOpen(true);
        }} className="flex items-center gap-2 text-sm sm:text-base px-3 py-2 sm:px-4 sm:py-2 whitespace-nowrap">
          <Plus className="w-4 h-4 flex-shrink-0" />
          <span className="hidden sm:inline">Create {activeTab === 'membership' ? 'Boost Membership' : activeTab === 'listing' ? 'Listing Credit' : 'Shop'} Coupon</span>
          <span className="sm:hidden">Create</span>
        </Button>
      </div>

      {/* Tabs */}
      <div className="mb-6 flex gap-2 border-b flex-wrap">
        <button
          onClick={() => setActiveTab('membership')}
          className={`px-4 sm:px-6 py-3 font-semibold transition-colors ${
            activeTab === 'membership'
              ? 'text-red-600 border-b-2 border-purple-600'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <span className="flex items-center gap-2">
            🎫 <span className="hidden sm:inline">Boost</span> Membership
            <Badge variant="secondary">{coupons.filter(c => c.coupon_type === 'membership').length}</Badge>
          </span>
        </button>
        <button
          onClick={() => setActiveTab('listing')}
          className={`px-4 sm:px-6 py-3 font-semibold transition-colors ${
            activeTab === 'listing'
              ? 'text-red-600 border-b-2 border-purple-600'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <span className="flex items-center gap-2">
            📋 Listing <span className="hidden sm:inline">Credit</span>
            <Badge variant="secondary">{coupons.filter(c => c.coupon_type === 'listing').length}</Badge>
          </span>
        </button>
        <button
          onClick={() => setActiveTab('shop')}
          className={`px-4 sm:px-6 py-3 font-semibold transition-colors ${
            activeTab === 'shop'
              ? 'text-red-600 border-b-2 border-purple-600'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <span className="flex items-center gap-2">
            🛍️ Shop
            <Badge variant="secondary">{coupons.filter(c => c.coupon_type === 'shop').length}</Badge>
          </span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">Total Coupons</div>
            <div className="text-3xl font-bold">{filteredCoupons.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">Active</div>
            <div className="text-3xl font-bold text-green-600">
              {filteredCoupons.filter(c => getCouponStatus(c).label === 'Active').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">Expired</div>
            <div className="text-3xl font-bold text-red-600">
              {filteredCoupons.filter(c => getCouponStatus(c).label === 'Expired').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">Total Uses</div>
            <div className="text-3xl font-bold text-blue-600">
              {filteredCoupons.reduce((sum, c) => sum + (c.current_uses || 0), 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Export Buttons */}
      <ExportButtons 
        data={filteredCoupons}
        columns={[
          { header: 'Coupon ID', key: 'id', accessor: (row) => row.id ? row.id.substring(0, 8) + '...' : 'N/A' },
          { header: 'Name', key: 'name', accessor: (row) => row.name || 'N/A' },
          { header: 'Code', key: 'code', accessor: (row) => row.code || 'N/A' },
          { header: 'Type', key: 'coupon_type', accessor: (row) => row.coupon_type || 'N/A' },
          { header: 'Discount', key: 'discount_value', accessor: (row) => row.discount_type === 'percentage' ? `${row.discount_value}%` : `$${row.discount_value}` },
          { header: 'Discount Type', key: 'discount_type', accessor: (row) => row.discount_type === 'percentage' ? 'Percentage Discount' : 'Cash Discount' },
          { header: 'Uses', key: 'uses', accessor: (row) => `${row.current_uses || 0}${row.max_uses ? ' / ' + row.max_uses : ' / ∞'}` },
          { header: 'Expires', key: 'expires_at', accessor: (row) => row.expires_at ? new Date(row.expires_at).toLocaleDateString() : 'No expiration' },
          { header: 'Status', key: 'effective_status', accessor: (row) => row.effective_status || (row.active ? 'Active' : 'Inactive') }
        ]}
        filename={`${activeTab}-coupons`}
      />

      {/* Coupons Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Ticket className="w-5 h-5" />
            {activeTab === 'membership' ? 'Boost Membership Coupons' : activeTab === 'listing' ? 'Listing Credit Coupons' : 'Shop Coupons'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredCoupons.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <Ticket className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No {activeTab === 'membership' ? 'boost membership' : activeTab === 'listing' ? 'listing credit' : 'shop'} coupons yet. Create your first coupon!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Coupon ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Coupon Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Coupon Code</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Coupon Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Coupon Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Uses</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Expires</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredCoupons.map((coupon) => {
                    const status = getCouponStatus(coupon);
                    return (
                    <tr key={coupon.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${status.label === 'Expired' ? 'opacity-60' : ''}`}>
                      <td className="px-4 py-3 text-sm">{coupon.id.substring(0, 8)}...</td>
                      <td className="px-4 py-3 text-sm font-medium">{coupon.name}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="font-mono">{coupon.code}</Badge>
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-green-600">
                        {coupon.discount_type === 'percentage' 
                          ? `${coupon.discount_value}%` 
                          : `$${coupon.discount_value}`
                        }
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {coupon.discount_type === 'percentage' ? 'Percentage Discount' : 'Cash Discount'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {coupon.current_uses || 0}
                        {coupon.max_uses ? ` / ${coupon.max_uses}` : ' / ∞'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {formatExpirationDate(coupon.expires_at)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={status.color}>
                          {status.label}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(coupon)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setCouponToDelete(coupon);
                              setDeleteConfirmOpen(true);
                            }}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={isCreateModalOpen || isEditModalOpen} onOpenChange={(open) => {
        if (!open) {
          setIsCreateModalOpen(false);
          setIsEditModalOpen(false);
          setEditingCoupon(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}</DialogTitle>
            <DialogDescription>
              {editingCoupon ? 'Update coupon details' : 'Create a new discount coupon for memberships'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2">
              <Label>Coupon Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Summer Sale 2024"
              />
            </div>

            <div className="col-span-2">
              <Label>Coupon Type *</Label>
              <Select
                value={formData.coupon_type}
                onValueChange={(value) => setFormData({ ...formData, coupon_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="membership">
                    <span className="flex items-center gap-2">
                      🎫 Boost Membership Coupon
                    </span>
                  </SelectItem>
                  <SelectItem value="listing">
                    <span className="flex items-center gap-2">
                      📋 Listing Credit Coupon
                    </span>
                  </SelectItem>
                  <SelectItem value="shop">
                    <span className="flex items-center gap-2">
                      🛍️ Shop Coupon
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {formData.coupon_type === 'membership' 
                  ? 'This coupon can be used for Boost Package purchases only'
                  : formData.coupon_type === 'listing'
                  ? 'This coupon can be used for Listing Credit purchases only'
                  : 'This coupon can be used for shop product purchases'}
              </p>
            </div>

            <div className="col-span-2">
              <Label>Coupon Code *</Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="e.g., SUMMER20"
                className="font-mono uppercase"
              />
            </div>

            <div>
              <Label>Discount Type *</Label>
              <Select
                value={formData.discount_type}
                onValueChange={(value) => setFormData({ ...formData, discount_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage</SelectItem>
                  <SelectItem value="cash">Fixed Amount</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Discount Value *</Label>
              <Input
                type="number"
                value={formData.discount_value}
                onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                placeholder={formData.discount_type === 'percentage' ? '20' : '10.00'}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {formData.discount_type === 'percentage' ? 'Percentage (0-100)' : 'Amount in CAD'}
              </p>
            </div>

            <div>
              <Label>Max Uses</Label>
              <Input
                type="number"
                value={formData.max_uses}
                onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                placeholder="Unlimited"
              />
            </div>

            <div>
              <Label>Expires At</Label>
              <Input
                type="datetime-local"
                value={formData.expires_at}
                onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
              />
            </div>

            <div>
              <Label>Minimum Purchase (CAD)</Label>
              <Input
                type="number"
                value={formData.min_purchase}
                onChange={(e) => setFormData({ ...formData, min_purchase: e.target.value })}
                placeholder="0"
              />
            </div>

            <div>
              <Label>Applicable To</Label>
              <Select
                value={formData.applicable_to}
                onValueChange={(value) => setFormData({ ...formData, applicable_to: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Memberships</SelectItem>
                  <SelectItem value="listing">Regular Listing Only</SelectItem>
                  <SelectItem value="boosted">Boosted Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsCreateModalOpen(false);
              setIsEditModalOpen(false);
              setEditingCoupon(null);
              resetForm();
            }}>
              Cancel
            </Button>
            <Button onClick={editingCoupon ? handleUpdateCoupon : handleCreateCoupon}>
              {editingCoupon ? 'Update Coupon' : 'Create Coupon'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Coupon</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the coupon "{couponToDelete?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteCoupon}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CouponManager;
